/**
 * De HTTP-client naar het platform.
 *
 * Wat hier weg is: de vaste `X-Tenant-Id`-, `X-User`- en `X-Roles`-headers uit
 * de v0.7-stub. Die kozen hun eigen tenant, en de backend weigert ze sinds
 * 2026-08-05 buiten `ENVIRONMENT=development`. Wat ervoor in de plaats komt is
 * een `TokenProvider` (ADR-003): standaard anoniem, lokaal een expliciet
 * ingestelde ontwikkelidentiteit, straks Entra ID.
 */
import axios, { type AxiosInstance, isAxiosError } from "axios";

import { type TokenProvider, createAnonymousTokenProvider } from "@/auth/tokenProvider";
import { DATA_VERSION_HEADER, observeDataVersion, parseVersionHeader, tagWithVersion } from "@/lib/dataVersion";

const DEFAULT_BASE_URL = "http://localhost:8000/api/v1";

/**
 * De startwaarde is bewust de anonieme provider en niet `createTokenProvider()`.
 *
 * Twee redenen. (1) Fail-closed: zolang niemand een provider heeft gezet, gaat
 * er geen enkel credential mee. (2) `createTokenProvider()` kán gooien — de
 * Entra-implementatie weigert te bestaan zonder client-id — en dat zou hier,
 * tijdens het laden van de module, een wit scherm opleveren zonder enige
 * uitleg. Nu bouwt `AuthProvider` de provider bij het opstarten, vangt die fout
 * en zet hem als leesbare melding op het inlogscherm.
 */
let tokenProvider: TokenProvider = createAnonymousTokenProvider();

/** Vervangt de actieve provider — gebruikt bij het opstarten en in tests. */
export const setTokenProvider = (provider: TokenProvider): void => {
  tokenProvider = provider;
};

export const getTokenProvider = (): TokenProvider => tokenProvider;

/** Wordt geroepen zodra de API een aanroep met 401 afwijst. */
type UnauthorizedHandler = (detail: string | null) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registreert wie er iets moet doen bij een 401. `AuthProvider` hangt zich
 * hieraan: een 401 terwijl er iemand is ingelogd betekent dat de backend het
 * token niet accepteert, en dat verdient een eigen scherm in plaats van een
 * foutmelding op elk paneel.
 */
export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null): void => {
  unauthorizedHandler = handler;
};

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL,
  headers: { Accept: "application/json" },
  // Een filter met meer dan één waarde reist als herhaalde sleutel:
  // `energy_label=E&energy_label=F`. Axios maakt daar standaard
  // `energy_label[]=E` van, en dat leest FastAPI als een andere parameter —
  // het filter zou dan stil niets doen. `indexes: null` zet dat recht.
  paramsSerializer: { indexes: null },
});

api.interceptors.request.use(async (config) => {
  const token = await tokenProvider.getAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  // De ontwikkeladapter uit ADR-003. Levert alleen iets op wanneer de
  // ontwikkelaar zelf VITE_DEV_TENANT_ID heeft gezet, en bestaat niet in een
  // productiebuild — createLocalTokenProvider weigert daar te bestaan.
  const devHeaders = tokenProvider.getDevHeaders?.() ?? {};
  for (const [name, value] of Object.entries(devHeaders)) {
    config.headers.set(name, value);
  }

  return config;
});

/**
 * Alleen 401 gaat naar de bewaking, niet 403.
 *
 * 401 = "ik weet niet wie je bent": het token ontbreekt, is verlopen, of mist
 * de tenantclaim uit ADR-003. Dat raakt de hele sessie.
 * 403 = "ik weet wie je bent, maar dit mag je niet": een `viewer` die iets
 * probeert te wijzigen. Die hoort ingelogd te blijven, met een melding op het
 * scherm zelf. Uitloggen zou hier het verkeerde antwoord zijn.
 */
api.interceptors.response.use(
  (response) => {
    // De dataversie van een cachebaar antwoord (plan 17, E4): in de store, en
    // onzichtbaar aan de data, zodat de querycache weet onder welke versie het
    // antwoord binnenkwam. Ook na een 304 levert de browser hier het bewaarde
    // antwoord met de koppen van de 304, dus met de actuele versie.
    const version = parseVersionHeader(response.headers[DATA_VERSION_HEADER]);
    if (version !== undefined) {
      observeDataVersion(version);
      tagWithVersion(response.data, version);
    }
    return response;
  },
  (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
      unauthorizedHandler?.(typeof detail === "string" ? detail : null);
    }
    return Promise.reject(error);
  },
);

/**
 * Laat de API zijn bewaarde antwoorden in deze browser wissen
 * (`Clear-Site-Data: "cache"`, plan 17 E3). De browser bewaart de cachebare
 * schermen met `Cache-Control: private` op schijf; bij uitloggen hoort dat weg.
 * Mislukt het (geen netwerk, token al verlopen), dan gaat het uitloggen door.
 */
export async function clearApiCache(): Promise<void> {
  try {
    await api.post("/logout");
  } catch {
    // Uitloggen mag hier nooit op stranden.
  }
}
